import React, { useEffect, useState } from "react";
import { formatDate } from "../utils/formatDate";
import { FaRegUser, FaUsers } from "react-icons/fa6";
import { RiFilePaperLine } from "react-icons/ri";
import { FaRegStickyNote } from "react-icons/fa";
import { LuFolderCheck } from "react-icons/lu";
import { FaArrowRight, FaChartBar, FaRotate } from "react-icons/fa6";
import Chart from "./Chart";
import axios from "axios";
import "../App.css";
import "../Styles/Dashboard.css";
import { useNavigate } from "react-router-dom";

function DashboardContent() {
  const [stats, setStats] = useState({
    todayUsers: 0,
    modules: 0,
    questions: 0,
    answers: 0,
    registerUsers: 0,
    participantCount: 0,
    participantPercentage: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const navigate = useNavigate();

  const currentDate = `${new Date().toLocaleDateString("en-US", {
    weekday: "long",
  })}, ${formatDate(new Date())}`;

  useEffect(() => {
    const fetchCounts = async () => {
      setIsLoading(true);
      setError("");
      try {
        const [loginRes, modulesRes, questionsRes, answersRes, registersRes] =
          await Promise.all([
            axios.get(`${process.env.REACT_APP_API}/getLoginNumCount`),
            axios.get(`${process.env.REACT_APP_API}/getModuleNumCount`),
            axios.get(`${process.env.REACT_APP_API}/getQuestionNumCount`),
            axios.get(`${process.env.REACT_APP_API}/getAnswerNumCount`),
            axios.get(`${process.env.REACT_APP_API}/getRegisterNumCount`),
          ]);

        setStats({
          todayUsers: loginRes.data.count || 0,
          modules: modulesRes.data.count || 0,
          questions: questionsRes.data.count || 0,
          answers: answersRes.data.count || 0,
          registerUsers: registersRes.data.registerUsers || 0,
          participantCount: registersRes.data.participantCount || 0,
          participantPercentage: registersRes.data.participantPercentage || 0,
        });
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
        setError("We couldn't load the latest dashboard totals. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCounts();
  }, [refreshKey]);

  const formatNumber = (number) => Number(number || 0).toLocaleString("en-US");

  const safePercentage = Math.min(
    Math.max(Number(stats.participantPercentage || 0), 0),
    100
  );

  const SmallCard = ({ title, value, icon, note, tone }) => (
    <article className={`dashboard-stat-card dashboard-stat-card--${tone}`}>
      <span className="dashboard-stat-icon" aria-hidden="true">{icon}</span>
      <div className="dashboard-stat-copy">
        <p className="dashboard-stat-title">{title}</p>
        {isLoading ? (
          <span className="dashboard-skeleton dashboard-skeleton--value" />
        ) : (
          <p className="dashboard-stat-value">{formatNumber(value)}</p>
        )}
        <p className="dashboard-stat-note">{note}</p>
      </div>
    </article>
  );

  return (
    <section className="dashboard-overview" aria-labelledby="dashboard-title">
      <header className="dashboard-header">
        <div>
          <p className="dashboard-eyebrow">ADMIN OVERVIEW</p>
          <h1 id="dashboard-title">Dashboard</h1>
          <p className="dashboard-intro">Monitor activity, participation, and assessment performance at a glance.</p>
        </div>
        <div className="dashboard-date" aria-label={`Today is ${currentDate}`}>
          <span>Today</span>{currentDate}
        </div>
      </header>

      {error && (
        <div className="dashboard-alert" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => setRefreshKey((key) => key + 1)}>
            <FaRotate aria-hidden="true" /> Retry
          </button>
        </div>
      )}

      <div className="dashboard-summary-grid">
        <div className="dashboard-stats-grid">
          <SmallCard title="Active today" value={stats.todayUsers} icon={<FaRegUser />} note="Users logged in today" tone="blue" />
          <SmallCard title="Modules" value={stats.modules} icon={<LuFolderCheck />} note="Available learning modules" tone="green" />
          <SmallCard title="Questions" value={stats.questions} icon={<RiFilePaperLine />} note="Questions in the library" tone="amber" />
          <SmallCard title="Answers" value={stats.answers} icon={<FaRegStickyNote />} note="Answers submitted" tone="rose" />
        </div>

        <article className="dashboard-participation-card">
          <div className="dashboard-participation-topline">
            <span className="dashboard-participation-icon" aria-hidden="true"><FaUsers /></span>
            <span className="dashboard-status-pill">Overall</span>
          </div>
          <div>
            <p className="dashboard-stat-title">Assessment participants</p>
            {isLoading ? (
              <span className="dashboard-skeleton dashboard-skeleton--participant" />
            ) : (
              <p className="dashboard-participation-value">{formatNumber(stats.participantCount)}</p>
            )}
            <p className="dashboard-participation-note">of {formatNumber(stats.registerUsers)} registered users</p>
          </div>
          <div className="dashboard-progress-block">
            <div className="dashboard-progress-label">
              <span>Participation rate</span><strong>{safePercentage}%</strong>
            </div>
            <div className="dashboard-progress-track" role="progressbar" aria-label="Assessment participation" aria-valuemin="0" aria-valuemax="100" aria-valuenow={safePercentage}>
              <span style={{ width: `${safePercentage}%` }} />
            </div>
          </div>
        </article>
      </div>

      <section className="dashboard-analytics" aria-labelledby="analytics-title">
        <div className="dashboard-section-header">
          <div>
            <p className="dashboard-eyebrow">REPORTING</p>
            <h2 id="analytics-title">Analytics overview</h2>
            <p>Compare participation and view your highest-performing users.</p>
          </div>
          <button className="dashboard-analytics-link" type="button" onClick={() => navigate("/analytics")}>
            <FaChartBar aria-hidden="true" /> Full analytics <FaArrowRight aria-hidden="true" />
          </button>
        </div>
        <Chart />
      </section>
    </section>
  );
}

export default DashboardContent;
